import { FieldType, type ProductFieldConfig } from "@thenounproject/lingo-core";

export type BaseFieldType =
  | "text"
  | "number"
  | "select"
  | "multiselect"
  | "date"
  | "boolean"
  | "asset";

export type FieldTypeDefinition = {
  baseType: BaseFieldType;
  label: string;
  description: string;
  /** Can only appear once per product type */
  isUnique?: boolean;
  /** Options are controlled by the system, not editable by the user */
  isManaged?: boolean;
  /** Type selector and visibility toggle are disabled; field is a built-in system default */
  isLocked?: boolean;
  isRequired?: boolean;
  /** Config values that override stored field config; these define invariant characteristics of the type */
  overrideConfig?: Partial<ProductFieldConfig>;
};

export const FIELD_TYPE_REGISTRY: Record<FieldType, FieldTypeDefinition> = {
  select: {
    baseType: "select",
    label: "Single-select",
    description:
      'For selecting a single value from a list of options. E.g. Changing a status to "approved".',
  },
  multiselect: {
    baseType: "multiselect",
    label: "Multi-select",
    description: "For selecting multiple values from a list of options.",
  },
  date: {
    baseType: "date",
    label: "Date",
    description: "For setting a date on products.",
  },
  number: {
    baseType: "number",
    label: "Number",
    description: "For adding any numerical value.",
  },
  text: {
    baseType: "text",
    label: "Text field",
    description: "For adding additional information, e.g. Campaign name, Product SKU",
  },
  boolean: {
    baseType: "boolean",
    label: "Boolean",
    description: "For true/false or yes/no values.",
  },
  name: {
    baseType: "text",
    label: "Name",
    description: "For the product name.",
    isUnique: true,
    isLocked: true,
    isRequired: true,
    overrideConfig: { multiline: false },
  },
  description: {
    baseType: "text",
    label: "Description",
    description: "For product description text.",
    isUnique: true,
    isLocked: true,
    isRequired: true,
    overrideConfig: { multiline: true },
  },
  sku: {
    baseType: "text",
    label: "SKU",
    description: "For product SKU/identifier.",
    isUnique: true,
    isLocked: true,
    isRequired: true,
    overrideConfig: { multiline: false },
  },
  price: {
    baseType: "number",
    label: "Price",
    description: "For product pricing information.",
    isUnique: true,
    isLocked: true,
    isRequired: true,
    overrideConfig: { numberType: "currency" },
  },
  asset: {
    baseType: "asset",
    label: "Asset",
    description: "For attaching one or more assets to a product.",
  },
  images: {
    baseType: "asset",
    label: "Images",
    description: "The canonical set of product images, used as the product preview.",
    isUnique: true,
    isLocked: true,
    isRequired: true,
  },
  cannabis_subcategory: {
    baseType: "select",
    label: "Cannabis Subcategory",
    description: "System-managed cannabis product subcategories with vendor mappings.",
    isUnique: true,
    isManaged: true,
  },
  phenotype: {
    baseType: "select",
    label: "Phenotype",
    description: "System-managed cannabis phenotype classification (indica, sativa, hybrid).",
    isUnique: true,
    isManaged: true,
  },
  taste: {
    baseType: "text",
    label: "Taste",
    description: "For describing product taste/flavor profile.",
    isUnique: true,
    isLocked: true,
  },
  mood: {
    baseType: "text",
    label: "Mood",
    description: "For describing product mood/effect profile.",
    isUnique: true,
    isLocked: true,
  },
  ingredients: {
    baseType: "text",
    label: "Ingredients",
    description: "For listing product ingredients.",
    isUnique: true,
    isLocked: true,
  },
  terpenes: {
    baseType: "text",
    label: "Terpenes",
    description: "For describing product terpene profile.",
    isUnique: true,
    isLocked: true,
  },
};

export function getBaseFieldType(fieldType: FieldType | undefined): BaseFieldType {
  return fieldType ? (FIELD_TYPE_REGISTRY[fieldType]?.baseType ?? "text") : "text";
}

export function getEffectiveConfig(
  fieldType: FieldType | undefined,
  storedConfig?: ProductFieldConfig
): ProductFieldConfig {
  return {
    ...storedConfig,
    ...(fieldType ? (FIELD_TYPE_REGISTRY[fieldType]?.overrideConfig ?? {}) : {}),
  };
}
